/**
 * Generic utility for extracting typed data parts from assistant-ui message metadata.
 *
 * Data parts are emitted by the server (chat.ts) as AI SDK data stream protocol
 * items and appear in message.metadata.unstable_data as objects with
 * { type: string, content: T }.
 */

/**
 * Extract the content of the first data part matching the given type string.
 * Returns null if no matching part is found.
 */
export function extractDataPart<T>(
	metadata: Record<string, unknown> | undefined,
	type: string,
): T | null {
	const dataParts = (metadata?.unstable_data ?? []) as unknown[]

	for (const part of dataParts) {
		if (
			part &&
			typeof part === 'object' &&
			'type' in part &&
			part.type === type &&
			'content' in part
		) {
			return (part as { type: string; content: T }).content
		}
	}
	return null
}

/**
 * Extract the content of the LAST data part matching the given type string.
 * Used when multiple emissions of the same type occur during streaming
 * (e.g. imLoading true then false, or an early and a final answerRef).
 */
export function extractLastDataPart<T>(
	metadata: Record<string, unknown> | undefined,
	type: string,
): T | null {
	const dataParts = (metadata?.unstable_data ?? []) as unknown[]

	let last: T | null = null
	for (const part of dataParts) {
		if (
			part &&
			typeof part === 'object' &&
			'type' in part &&
			part.type === type &&
			'content' in part
		) {
			last = (part as { type: string; content: T }).content
		}
	}
	return last
}
